import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plug, PowerOff, Settings2, RefreshCw, CheckCircle2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function DhanCard({ status, reload }) {
  useEffect(() => {
    if (status?.client_id) {
      setForm((s) => ({ ...s, client_id: status.client_id }));
      setTotpForm((s) => ({ ...s, client_id: status.client_id }));
    }
  }, [status?.client_id]);

  const [setupOpen, setSetupOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", access_token: "" });
  const [totpOpen, setTotpOpen] = useState(false);
  const [totpForm, setTotpForm] = useState({ client_id: "", pin: "", totp_secret: "", api_client_id: "" });
  const [busy, setBusy] = useState(false);

  const hasCreds = status?.has_credentials;
  const isAuth = status?.is_authenticated;
  const tokenExpiresAt = status?.token_expires_at;
  const tokenExpired = status?.token_expired;
  const autoAuth = status?.auto_auth_configured;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/dhan/credentials", form);
      toast.success("Dhan credentials saved");
      setSetupOpen(false);
      setForm({ client_id: "", access_token: "" });
      reload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    setBusy(true);
    try {
      await api.post("/dhan/connect");
      toast.success("Dhan connected");
      reload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dhan connect failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/dhan/disconnect");
      toast.success("Dhan disconnected");
      reload?.();
    } catch (err) {
      toast.error("Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  const wipe = async () => {
    if (!window.confirm("Delete saved Dhan credentials?")) return;
    try {
      await api.delete("/dhan/credentials");
      toast.success("Dhan credentials wiped");
      reload?.();
    } catch (err) {
      toast.error("Wipe failed");
    }
  };

  const renew = async () => {
    setBusy(true);
    try {
      await api.post("/dhan/renew");
      toast.success("Token renewed");
      reload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Renew failed");
    } finally {
      setBusy(false);
    }
  };

  const setupTotp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        client_id: totpForm.client_id,
        pin: totpForm.pin,
        totp_secret: totpForm.totp_secret,
      };
      if (totpForm.api_client_id?.trim()) {
        payload.api_client_id = totpForm.api_client_id.trim();
      }
      await api.post("/dhan/totp-setup", payload);
      toast.success("Auto-renew enabled — token renewed daily at 08:00 IST");
      setTotpOpen(false);
      setTotpForm({ client_id: "", pin: "", totp_secret: "", api_client_id: "" });
      reload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "TOTP setup failed");
    } finally {
      setBusy(false);
    }
  };

  const disableTotp = async () => {
    if (!window.confirm("Disable Dhan auto-renew? Your current token stays valid.")) return;
    setBusy(true);
    try {
      await api.delete("/dhan/totp");
      toast.success("Auto-renew disabled");
      setTotpOpen(false);
      reload?.();
    } catch (err) {
      toast.error("Failed to disable auto-renew");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-surface-2 border-border rounded-sm" data-testid="dhan-card">
      <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1 font-semibold">
            / broker
          </div>
          <CardTitle className="text-lg font-medium">Dhan</CardTitle>
        </div>
        <StatusPill isAuth={isAuth} hasCreds={hasCreds} />
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3 pt-1">
          <Stat label="Credentials" value={hasCreds ? "saved" : "missing"} tone={hasCreds ? "ok" : "warn"} />
          <Stat label="Session" value={isAuth ? "active" : "inactive"} tone={isAuth ? "ok" : "warn"} />
          <TokenExpiryStat expiresAt={tokenExpiresAt} expired={tokenExpired} autoAuth={autoAuth} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSetupOpen(true)}
            data-testid="dhan-edit-creds"
            className="rounded-sm h-8 text-xs border-border bg-surface-1 hover:bg-surface-3"
          >
            <Settings2 className="w-3.5 h-3.5 mr-1.5" />
            {hasCreds ? "Edit token" : "Add token"}
          </Button>
          {hasCreds && !isAuth && (
            <Button
              size="sm"
              onClick={connect}
              disabled={busy}
              data-testid="dhan-connect-btn"
              className="rounded-sm h-8 text-xs bg-brand hover:bg-brand/90 text-white"
            >
              <Plug className="w-3.5 h-3.5 mr-1.5" />
              Connect
            </Button>
          )}
          {isAuth && (
            <Button
              size="sm"
              variant="ghost"
              onClick={disconnect}
              disabled={busy}
              data-testid="dhan-disconnect-btn"
              className="rounded-sm h-8 text-xs text-muted-foreground hover:text-loss"
            >
              <PowerOff className="w-3.5 h-3.5 mr-1.5" />
              Disconnect
            </Button>
          )}
          {autoAuth && (
            <Button
              size="sm"
              onClick={renew}
              disabled={busy}
              data-testid="dhan-renew-btn"
              className="rounded-sm h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Renew now
            </Button>
          )}
          {autoAuth && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setTotpOpen(true)}
              disabled={busy}
              data-testid="dhan-totp-edit-btn"
              className="rounded-sm h-8 text-xs border-border bg-surface-1 hover:bg-surface-3"
            >
              <KeyRound className="w-3.5 h-3.5 mr-1.5" />
              Edit auto-renew
            </Button>
          )}
          {!autoAuth && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setTotpOpen(true)}
              disabled={busy}
              data-testid="dhan-totp-setup-btn"
              className="rounded-sm h-8 text-xs border-border bg-surface-1 hover:bg-surface-3"
            >
              <KeyRound className="w-3.5 h-3.5 mr-1.5" />
              Auto-renew (TOTP)
            </Button>
          )}
          {hasCreds && (
            <Button
              size="sm"
              variant="ghost"
              onClick={wipe}
              disabled={busy}
              data-testid="dhan-wipe-btn"
              className="rounded-sm h-8 text-xs text-muted-foreground hover:text-loss ml-auto"
            >
              Wipe
            </Button>
          )}
        </div>
      </CardContent>

      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="bg-surface-2 border-border rounded-sm sm:max-w-lg" data-testid="dhan-setup-dialog">
          <DialogHeader>
            <DialogTitle className="font-medium tracking-tight">Dhan API Access</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Generate an access token at <span className="text-white">web.dhan.co → My Profile → Access DhanHQ APIs</span>.
              Default tokens expire every 24h unless your app is registered.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                Client ID <span className="text-loss">*</span>
              </Label>
              <Input
                required
                value={form.client_id}
                onChange={(e) => setForm((s) => ({ ...s, client_id: e.target.value }))}
                data-testid="dhan-client-id-input"
                placeholder="10 digit Dhan client id"
                className="h-9 rounded-sm bg-surface-1 border-border font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                Access Token <span className="text-loss">*</span>
              </Label>
              <Input
                required
                type="password"
                value={form.access_token}
                onChange={(e) => setForm((s) => ({ ...s, access_token: e.target.value }))}
                data-testid="dhan-access-token-input"
                className="h-9 rounded-sm bg-surface-1 border-border font-mono text-xs"
              />
              <div className="text-[10px] text-muted-foreground">
                Long JWT-like string. Regenerate daily or when expired.
              </div>
            </div>
            <DialogFooter className="pt-3">
              <Button type="button" variant="ghost" onClick={() => setSetupOpen(false)} className="rounded-sm h-9 text-xs">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="dhan-save-btn"
                className="rounded-sm h-9 text-xs bg-brand hover:bg-brand/90 text-white"
              >
                {busy ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={totpOpen} onOpenChange={setTotpOpen}>
        <DialogContent className="bg-surface-2 border-border rounded-sm sm:max-w-lg" data-testid="dhan-totp-dialog">
          <DialogHeader>
            <DialogTitle className="font-medium tracking-tight">Dhan auto-renew (TOTP)</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Set up automatic daily renewal. In Dhan: <span className="text-white">My Profile → TOTP/2FA → Reset TOTP</span>,
              scan the QR (or note the <span className="text-white">SKey / secret</span>) with your authenticator app.
              Your access token will renew automatically every day at 08:00 IST, so you never need to re-paste it.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={setupTotp} className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                Dhan Client ID <span className="text-loss">*</span>
              </Label>
              <Input
                required
                value={totpForm.client_id}
                onChange={(e) => setTotpForm((s) => ({ ...s, client_id: e.target.value }))}
                data-testid="totp-client-id-input"
                placeholder="10 digit Dhan client id"
                className="h-9 rounded-sm bg-surface-1 border-border font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                Dhan Login PIN <span className="text-loss">*</span>
              </Label>
              <Input
                required
                type="password"
                autoComplete="off"
                value={totpForm.pin}
                onChange={(e) => setTotpForm((s) => ({ ...s, pin: e.target.value }))}
                data-testid="totp-pin-input"
                placeholder="Your Dhan login PIN"
                className="h-9 rounded-sm bg-surface-1 border-border font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                TOTP Secret <span className="text-loss">*</span>
              </Label>
              <Input
                required
                type="password"
                autoComplete="off"
                value={totpForm.totp_secret}
                onChange={(e) => setTotpForm((s) => ({ ...s, totp_secret: e.target.value }))}
                data-testid="totp-secret-input"
                placeholder="Base32 secret from the Dhan SKey"
                className="h-9 rounded-sm bg-surface-1 border-border font-mono text-xs"
              />
              <div className="text-[10px] text-muted-foreground">
                Stored encrypted on the server. Only used to mint a new token each day — the live 6-digit code is never stored.
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                Numeric Client ID <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                value={totpForm.api_client_id}
                onChange={(e) => setTotpForm((s) => ({ ...s, api_client_id: e.target.value }))}
                data-testid="totp-api-client-id-input"
                placeholder="Numeric login client id (e.g. 1111393765)"
                className="h-9 rounded-sm bg-surface-1 border-border font-mono text-xs"
              />
              <div className="text-[10px] text-muted-foreground">
                Leave blank to use the deployment default.
              </div>
            </div>
            <DialogFooter className="pt-3">
              {autoAuth && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={disableTotp}
                  disabled={busy}
                  data-testid="dhan-totp-disable-btn"
                  className="mr-auto rounded-sm h-9 text-xs text-muted-foreground hover:text-loss"
                >
                  Disable auto-renew
                </Button>
              )}
              <Button type="button" variant="ghost" onClick={() => setTotpOpen(false)} className="rounded-sm h-9 text-xs">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="dhan-totp-save-btn"
                className="rounded-sm h-9 text-xs bg-brand hover:bg-brand/90 text-white"
              >
                {busy ? "Saving..." : autoAuth ? "Update" : "Enable auto-renew"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function StatusPill({ isAuth, hasCreds }) {
  let color = "#737373", text = "not configured";
  if (hasCreds && isAuth) { color = "#00C805"; text = "connected"; }
  else if (hasCreds) { color = "#FF9F0A"; text = "needs connect"; }
  return (
    <div className="flex items-center gap-2 border border-border px-2.5 h-7 rounded-sm bg-surface-1 font-mono text-[10px] uppercase tracking-wider">
      <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ backgroundColor: color }} />
      <span style={{ color }}>{text}</span>
    </div>
  );
}

function Stat({ label, value, tone = "default" }) {
  const color = tone === "ok" ? "text-profit" : tone === "warn" ? "text-warn" : "text-white";
  return (
    <div className="border border-border p-3 bg-surface-1 rounded-sm">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <div className={`mt-1 font-mono text-sm ${color}`}>{value}</div>
    </div>
  );
}

function TokenExpiryStat({ expiresAt, expired, autoAuth }) {
  if (!expiresAt) {
    return <Stat label="Token expiry" value="—" tone="default" />;
  }

  const dt = new Date(expiresAt);
  const now = new Date();
  const diffMs = dt - now;
  const isExpired = expired || diffMs <= 0;

  let value, tone;
  if (isExpired) {
    value = "expired";
    tone = "warn";
  } else {
    const hrs = Math.floor(diffMs / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    value = `${hrs}h ${mins}m`;
    tone = hrs < 2 ? "warn" : "ok";
  }

  return (
    <div className="border border-border p-3 bg-surface-1 rounded-sm">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
        Token expiry {autoAuth && <CheckCircle2 className="inline w-3 h-3 ml-1 text-profit" />}
      </div>
      <div className={`mt-1 font-mono text-sm flex items-center gap-1 ${tone === "ok" ? "text-profit" : "text-warn"}`}>
        {autoAuth && !isExpired && <CheckCircle2 className="w-3 h-3" />}
        {value}
      </div>
    </div>
  );
}
